import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import PageTitle from "./PageTitle";
import Spacer from "./Spacer";
import "./DemographicsPage.styles.css";
import { getParticipantIdFromSearchParams } from '../utils/urlParams';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

type UsdEarningsSummary = {
  totalUsd: number;
  roundUsd?: number[];
};

type RoundEarnings = {
  playerEarnings: number;
  partnerEarnings: number;
};

const GameCompletionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const participantId = useMemo(() => getParticipantIdFromSearchParams(location.search), [location.search]);
  const [usdEarnings, setUsdEarnings] = useState<UsdEarningsSummary | null>(null);
  const [roundEarnings, setRoundEarnings] = useState<RoundEarnings | null>(null);

  const handleContinue = () => {
    const queryParams = new URLSearchParams(location.search);
    const newUrl = '/post-game-other-opinion?' + queryParams.toString();
    console.log("GameCompletionPage: Navigating to:", newUrl);
    navigate(newUrl);
  };

  useEffect(() => {
    if (!API_BASE_URL || !participantId) return;

    let cancelled = false;
    fetch(`${API_BASE_URL}/checkPlayerStatus`, {
      method: 'POST',
      headers: { 'prolific-id': participantId },
    })
      .then((res) => res.json().then((payload) => ({ ok: res.ok, data: payload })))
      .then(({ ok, data }) => {
        if (cancelled || !ok) return;
        const earnings = data?.usdEarnings;
        const totalUsd = typeof earnings?.totalUsd === 'number' ? earnings.totalUsd : 0;
        setUsdEarnings({ totalUsd, roundUsd: earnings?.roundUsd });
      })
      .catch(() => {});

    fetch(`${API_BASE_URL}/roundHistory`, {
      method: 'GET',
      headers: { 'prolific-id': participantId },
    })
      .then((res) => res.json().then((payload) => ({ ok: res.ok, data: payload })))
      .then(({ ok, data }) => {
        if (cancelled || !ok) return;
        const rounds = Array.isArray(data?.rounds) ? data.rounds : [];
        if (rounds.length === 0) return;
        // roundHistory returns newest first; with one main round this is the investment result.
        const latest = rounds[0];
        const playerEarnings = Number(latest?.player?.earnings);
        const partnerEarnings = Number(latest?.partner?.earnings);
        if (Number.isFinite(playerEarnings) && Number.isFinite(partnerEarnings)) {
          setRoundEarnings({ playerEarnings, partnerEarnings });
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [participantId]);

  return (
    <div className="demographics-bg">
      <div className="demographics-card fade-in">
        <PageTitle title="Investment Results" />
        <Spacer />
        
        <div className="demographics-thankyou" style={{ fontSize: 20, marginBottom: 18 }}>
          Here are the results from your investment round.
        </div>

        {roundEarnings && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 24,
            flexWrap: 'wrap',
            margin: '0 auto 18px auto',
            maxWidth: 480,
          }}>
            <div style={{ background: '#D4F4E0', borderRadius: 12, padding: '12px 24px', minWidth: 120, textAlign: 'center' }}>
              <div style={{ fontSize: 15, color: '#4B5563', marginBottom: 4 }}>You earned</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#374151' }}>
                {roundEarnings.playerEarnings} <span role="img" aria-label="coin">💰</span>
              </div>
            </div>
            <div style={{ background: '#FDE3C9', borderRadius: 12, padding: '12px 24px', minWidth: 120, textAlign: 'center' }}>
              <div style={{ fontSize: 15, color: '#4B5563', marginBottom: 4 }}>Partner earned</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#374151' }}>
                {roundEarnings.partnerEarnings} <span role="img" aria-label="coin">💰</span>
              </div>
            </div>
          </div>
        )}

        {usdEarnings && usdEarnings.totalUsd > 0 && (
          <div style={{
            background: '#eef6ff',
            border: '1.5px solid #60a5fa',
            borderRadius: 12,
            padding: '18px 16px',
            margin: '0 auto 18px auto',
            maxWidth: 480,
            width: '100%',
            boxSizing: 'border-box',
            boxShadow: '0 2px 12px rgba(59, 130, 246, 0.08)',
            textAlign: 'center',
          }}>
            <div style={{ fontWeight: 800, color: '#0b4aa2', marginBottom: 8 }}>
              Total study bonus
            </div>
            <div style={{ fontWeight: 900, fontSize: 28, color: '#0b4aa2' }}>
              ${usdEarnings.totalUsd.toFixed(2)}
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 10, lineHeight: 1.45 }}>
              Converted from coins earned in the study.
            </div>
          </div>
        )}

        <div style={{ 
          fontSize: 15, 
          color: '#666', 
          marginTop: 8,
          background: '#f5f5f5',
          borderRadius: 8,
          padding: '12px 16px',
          border: '1px solid #e0e0e0',
          maxWidth: 480,
          margin: '0 auto 18px auto'
        }}>
          <strong>What happens next:</strong>
          <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
            <li>Share any final thoughts</li>
            <li>Complete the remaining questionnaires</li>
            <li>Receive your completion code</li>
          </ul>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button
            onClick={handleContinue}
            style={{
              background: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#388e3c';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#4caf50';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(76, 175, 80, 0.3)';
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameCompletionPage;
